import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
// Import assets
import Logo from "../../assets/logo.svg";

const NavLink = ({
  to,
  children,
  className = "",
  variant = "link",
  onClick,
  scrollToId = "",
}) => {
  const handleClick = (e) => {
    if (scrollToId) {
      e.preventDefault();
      const section = document.getElementById(scrollToId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (onClick) onClick();
  };

  const baseStyles = {
    link: "text-dark-blue-text hover:text-primary transition",
    buttonPrimary:
      "flex items-center justify-center text-purple-text rounded-md bg-background w-full h-full",
    buttonSecondary:
      "flex items-center justify-center text-white-text rounded-md bg-gradient-purple shadow-custom-blue",
  };

  return (
    <Link
      to={scrollToId ? "#" : to}
      onClick={handleClick}
      className={`${baseStyles[variant] || ""} ${className}`.trim()}
    >
      {children}
    </Link>
  );
};

const Navbar = ({ isTenantPayment: isTenantPaymentProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navbarRef = useRef(null);

  // Inside Navbar component
  const location = useLocation();
  const navigate = useNavigate();

  const isTenantPayment =
    typeof isTenantPaymentProp === "boolean"
      ? isTenantPaymentProp
      : /^\/make-payment\/[^/]+\/[^/]+$/.test(location.pathname);
      
  const handleSectionLink = (sectionId) => {
    const offset = 100;
  
    if (sectionId === "home") {
      // Scroll to the top of the page directly
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      if (location.pathname !== "/") {
        // Navigate to home first, then scroll with offset
        navigate("/", { replace: false });
        // Delay to wait for homepage to mount
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          if (section) {
            window.scrollTo({ top: section.offsetTop - offset, behavior: "smooth" });
          }
        }, 100);
      } else {
        const section = document.getElementById(sectionId);
        if (section) {
          window.scrollTo({ top: section.offsetTop - offset, behavior: "smooth" });
        }
      }
    }
  };

  //Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <div
        ref={navbarRef}
        className={`z-50 flex justify-center w-full ${
          isTenantPayment
            ? "bg-[#F6FCFF]"
            : isOpen ? "bg-background" : "bg-white/50 backdrop-blur-md"
        }`}
      >
        <div className="flex justify-between items-center max-w-[1250px] w-10/12 h-[60px] ">
          {/* Logo */}
          <div className="">
            <Link
              to="/"
              onClick={() => handleSectionLink("home")}
              className="flex items-center justify-center"
            >
              <img
                src={Logo}
                alt="Tenafix Logo"
                className="w-[100px] h-[50px] sm:w-[158px] sm:h-[50px] lg:w-[168px] lg:h-[60px]"
              />
            </Link>
          </div>

          {/* Desktop Links */}
          <div className="hidden w-full font-semibold md:flex md:items-center items-center md:justify-between md:space-x-8 lg:space-x-12 xl:space-x-18 font-inter">
            <div className="flex justify-end w-1/2 gap-5 pl-5 lg:gap-10 lg:pl-12 md:ml-[48px] lg:ml-[78px]">
              <NavLink
                to="/"
                className="font-semibold text-level-6 xl:text-lg"
                onClick={() => handleSectionLink("home")}
              >
                Home
              </NavLink>
              <NavLink
                to="/"
                className="font-semibold text-level-6 xl:text-lg"
                onClick={() => handleSectionLink("aboutus")}
              >
                About Us
              </NavLink>

              <NavLink
                to="/"
                className="font-semibold text-level-6 xl:text-lg"
                onClick={() => handleSectionLink("contactus")}
              >
                Contact Us
              </NavLink>
            </div>
            <div className="flex items-center justify-around w-1/4 lg:w-1/4">
              <div className="bg-gradient-purple md:h-[35px] md:w-[70px] xl:h-[46px] xl:w-[115px] relative rounded-[8px] p-0.5">
                <NavLink
                  to="/signup"
                  variant="buttonPrimary"
                  className="font-semibold text-center font-alexandria text-level-7 xl:text-level-5 "
                >
                  Sign Up
                </NavLink>
              </div>
              <NavLink
                to="/signin"
                variant="buttonSecondary"
                className="font-alexandria font-semibold mr-0 lg:mr-[-15px] text-level-7 xl:text-level-5 text-center h-[40px] md:h-[35px] xl:h-[46px] w-[65px] xl:w-[115px]"
              >
                Login
              </NavLink>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-dark" />
              ) : (
                <Menu className="w-6 h-6 text-dark" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`
                      fixed inset-0
                      flex flex-col justify-between
                      bg-background
                      px-4 py-6
                      md:hidden
                      font-inter font-semibold
                      transition-all duration-300 ease-in-out overflow-y-hidden 
                      ${isOpen ? "opacity-100 visible " : "opacity-0 invisible"}
                    `}
        style={{
          top: `${navbarRef.current?.getBoundingClientRect().bottom || 84}px`,
        }}
      >
        {/* Top Group */}
        <div className="mt-4 space-y-4">
          <NavLink
            to="/"
            className="block px-3 py-2 rounded-md"
            onClick={() => {
              handleSectionLink("home");
              setIsOpen(false);
            }}
          >
            Home
          </NavLink>
          <NavLink
            to="/"
            className="block px-3 py-2 rounded-md"
            onClick={(e) => {
              handleSectionLink("aboutus");
              setIsOpen(false);
            }}
          >
            About Us
          </NavLink>
          <NavLink
            to="/"
            className="block px-3 py-2 rounded-md"
            onClick={(e) => {
              handleSectionLink("contactus");
              setIsOpen(false);
            }}
          >
            Contact Us
          </NavLink>
        </div>

        {/* Bottom Group */}
        <div className="space-y-3 font-alexandria">
          <div className="bg-gradient-purple relative w-full h-12 rounded-[8px] p-0.5">
            <NavLink
              to="/signup"
              variant="buttonPrimary"
              onClick={() => setIsOpen(false)}
            >
              Sign Up
            </NavLink>
          </div>
          <NavLink
            to="/signin"
            variant="buttonSecondary"
            className="w-full h-12"
            onClick={() => setIsOpen(false)}
          >
            Login
          </NavLink>
        </div>
      </div>
    </>
  );
};

export default Navbar;
